import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectItem } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  FileText,
  Upload,
  Loader2,
  FileImage,
  File,
  Eye,
  Download,
  X,
  Search,
} from 'lucide-react';
import type { ApiResponse, Document, DocumentType } from '@/types';

const DOC_TYPE_LABELS: Record<DocumentType, { label: string; icon: React.ReactNode; color: string }> = {
  contract: { label: 'Contract', icon: <FileText className="h-4 w-4" />, color: 'bg-blue-500' },
  plan: { label: 'Plan', icon: <FileImage className="h-4 w-4" />, color: 'bg-purple-500' },
  invoice: { label: 'Invoice', icon: <FileText className="h-4 w-4" />, color: 'bg-amber-500' },
  photo: { label: 'Photo', icon: <FileImage className="h-4 w-4" />, color: 'bg-emerald-500' },
  report: { label: 'Report', icon: <FileText className="h-4 w-4" />, color: 'bg-orange-500' },
  other: { label: 'Other', icon: <File className="h-4 w-4" />, color: 'bg-slate-500' },
};

const DOC_TYPES = Object.keys(DOC_TYPE_LABELS) as DocumentType[];

export function DocumentsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [docType, setDocType] = useState<DocumentType>('other');
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const { data: documents, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: async (): Promise<Document[]> => {
      const res: ApiResponse<Document[]> = await api.get('/documents');
      return res.success ? (res.data ?? []) : [];
    },
  });

  const { data: projects } = useQuery({
    queryKey: ['projects-for-docs'],
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const res = await api.get<{ id: string; name: string }[]>('/projects');
      return res.success ? (res.data ?? []) : [];
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setIsUploading(true);
    try {
      await api.post('/documents/upload', {
        name: selectedFile.name,
        type: docType,
        projectId: selectedProjectId || undefined,
        mimeType: selectedFile.type,
        sizeBytes: selectedFile.size,
      });
      toast.success('Document uploaded successfully');
      setShowUpload(false);
      setSelectedFile(null);
      setDocType('other');
      setSelectedProjectId('');
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const filteredDocs = (documents ?? []).filter((doc) => {
    if (searchTerm && !doc.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterType && doc.type !== filterType) return false;
    return true;
  });

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="text-muted-foreground">Document hub and file management</p>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          <Upload className="mr-2 h-4 w-4" /> Upload
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType} placeholder="All Types">
          <SelectItem value="">All Types</SelectItem>
          {DOC_TYPES.map((t) => (
            <SelectItem key={t} value={t}>{DOC_TYPE_LABELS[t].label}</SelectItem>
          ))}
        </Select>
        {(searchTerm || filterType) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearchTerm(''); setFilterType(''); }}
          >
            <X className="mr-1 h-3 w-3" /> Clear
          </Button>
        )}
      </div>

      {/* Document Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="mb-3 h-24 w-full" />
                <Skeleton className="mb-2 h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredDocs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-50" />
            {documents && documents.length > 0 ? (
              <>
                <h3 className="text-lg font-medium">No matches</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  No documents match your filters
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium">No documents yet</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Upload your first document to get started
                </p>
                <Button className="mt-4" onClick={() => setShowUpload(true)}>
                  <Upload className="mr-2 h-4 w-4" /> Upload Document
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredDocs.map((doc) => {
            const typeInfo = DOC_TYPE_LABELS[doc.type] ?? DOC_TYPE_LABELS.other;
            return (
              <Card key={doc.id} className="group transition-all hover:border-primary/50">
                <CardContent className="p-4">
                  <div className="mb-3 flex items-center justify-center rounded-md bg-muted p-6">
                    <FileText className="h-10 w-10 text-muted-foreground" />
                  </div>
                  <h4 className="font-medium text-sm truncate">{doc.name}</h4>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {typeInfo.label}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(doc.sizeBytes)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {doc.aiSummary ? (
                      <Badge variant="secondary" className="text-xs">AI Summarized</Badge>
                    ) : null}
                    {doc.visibleToClient && (
                      <Badge variant="outline" className="text-xs">Client Visible</Badge>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs">
                      <Eye className="mr-1 h-3 w-3" /> View
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs">
                      <Download className="mr-1 h-3 w-3" /> Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>Select a file to upload to your document hub</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="docType">Document Type</Label>
            <Select value={docType} onValueChange={(v) => setDocType(v as DocumentType)}>
              {DOC_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{DOC_TYPE_LABELS[t].label}</SelectItem>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <Label>File</Label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <Button variant="outline" size="sm" asChild>
                  <span>
                    <Upload className="mr-2 h-4 w-4" />
                    Choose File
                  </span>
                </Button>
                <input type="file" className="hidden" onChange={handleFileSelect} />
              </label>
              {selectedFile && (
                <span className="text-sm text-muted-foreground">
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                </span>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowUpload(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={isUploading || !selectedFile}>
            {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Upload
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
